import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { MicroApiService } from '../constants/micro-api.service';

export interface BrandDocument {
  colors: {
    primary: string;
    secondary?: string;
    accent?: string;
    on_primary?: string;
    heading?: string;
    text?: string;
    text_muted?: string;
    bg?: string;
    surface?: string;
    surface_alt?: string;
    sheet?: string;
    gradient_start?: string;
    gradient_end?: string;
    card?: string;
    card_border?: string;
    card_alt?: string;
    task?: string;
    success?: string;
    warning?: string;
    error?: string;
    completed?: string;
    disabled?: string;
    grey_1?: string;
    grey_2?: string;
    grey_3?: string;
    grey_4?: string;
    grey_5?: string;
    grey_6?: string;
  };
  colors_dark?: Record<string, string>;
  typography?: {
    source: 'system' | 'google' | 'upload';
    family?: string | null;
    family_ar?: string | null;
    heading_family?: string | null;
    google_family?: string | null;
    google_family_ar?: string | null;
    fallback?: string;
    base_size?: number;
  };
  shape?: {
    radius?: number;
    radius_lg?: number;
    button_radius?: number;
    shell_max?: number;
  };
  identity?: {
    display_name?: string;
    display_name_ar?: string;
    tagline?: string;
    tagline_ar?: string;
  };
  meta?: {
    title?: string;
    title_ar?: string;
    description?: string;
    description_ar?: string;
  };
  support?: {
    email?: string;
    phone?: string;
    whatsapp?: string;
    website?: string;
  };
  social?: {
    instagram?: string;
    x?: string;
    tiktok?: string;
    snapchat?: string;
    youtube?: string;
    linkedin?: string;
  };
  legal?: {
    privacy_url?: string;
    terms_url?: string;
  };
  defaults?: {
    language?: string;
    theme?: string;
  };
  flags?: {
    powered_by?: boolean;
  };
}

export interface CreateBrandPayload {
  name: string;
  description?: string;
  brand: BrandDocument;
  logo?: any;
  logo_dark?: any;
  icon?: any;
  og_image?: any;
  fonts?: any[];
  is_default?: boolean;
}

export interface UpdateBrandPayload {
  id: number | string;
  name?: string;
  description?: string;
  brand?: BrandDocument;
  logo?: any;
  logo_dark?: any;
  icon?: any;
  og_image?: any;
  fonts?: any[];
  is_active?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BrandingService {
  // Brands and custom domains live on the gateway (2450-2458). Every call is fenced to the
  // signed-in business by the server — the same logic the admin portal drives through 8250-8259.
  // Ids are uuids, so they are typed `number | string` and never run through Number().
  private micro = inject(MicroApiService);

  getBrands(search?: string, includeInactive = true): Observable<any> {
    return this.micro.route('BRANDING_LIST', true, { search, include_inactive: includeInactive });
  }

  createBrand(payload: CreateBrandPayload): Observable<any> {
    return this.micro.route('BRANDING_CREATE', false, payload);
  }

  updateBrand(payload: UpdateBrandPayload): Observable<any> {
    return this.micro.route('BRANDING_UPDATE', false, payload);
  }

  setDefaultBrand(id: number | string): Observable<any> {
    return this.micro.route('BRANDING_SET_DEFAULT', false, { id });
  }

  // Domain CRUD
  getDomains(): Observable<any> {
    return this.micro.route('DOMAIN_LIST', true);
  }

  createDomain(payload: { hostname: string; branding_id: number | string; is_primary?: boolean; notes?: string; zero_storage?: boolean; zero_storage_key?: string }): Observable<any> {
    return this.micro.route('DOMAIN_CREATE', false, payload);
  }

  updateDomain(payload: { id: number | string; branding_id?: number | string; is_primary?: boolean; is_enabled?: boolean; notes?: string }): Observable<any> {
    return this.micro.route('DOMAIN_UPDATE', false, payload);
  }

  verifyDomain(id: number | string): Observable<any> {
    return this.micro.route('DOMAIN_VERIFY', false, { id });
  }

  deleteDomain(id: number | string): Observable<any> {
    return this.micro.route('DOMAIN_DELETE', false, { id });
  }
}
